import { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { profileStore } from '../stores/profileStore'
import { leaderboardStore } from '../stores/leaderboardStore'
import { gamificationStore } from '../stores/gamificationStore'
import { mojilandStore } from '../stores/mojilandStore'
import { playSound, vibrate } from '../utils/sounds'
import GameLeaderboard from '../components/GameLeaderboard'
import { quizTopics, getRandomCards, getAllCards, type FlashCard, type QuizTopic } from '../utils/quizData'
import { getRandomModuleCards } from '../courses/openWaterQuiz'
import { getModuleById } from '../courses/openWater'

const CARDS_PER_ROUND = 10
const TIME_PER_QUESTION = 15 // seconds
const AUTO_ADVANCE_DELAY_MS = 1800

/** Shuffle options so answer position is random each time */
const shuffleOptions = (card: FlashCard): FlashCard => {
  const shuffled = [...card.options].sort(() => Math.random() - 0.5)
  return { ...card, options: shuffled }
}

const QuizMoji = observer(() => {
  const [searchParams] = useSearchParams()
  const openWaterModuleId = searchParams.get('module')
  const [selectedTopic, setSelectedTopic] = useState<QuizTopic | null>(null)
  const [openWaterTopic, setOpenWaterTopic] = useState<QuizTopic | null>(null)
  const [currentCards, setCurrentCards] = useState<FlashCard[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [score, setScore] = useState(0)
  const [scoreGained, setScoreGained] = useState<number | null>(null)
  const [streak, setStreak] = useState(0)
  const [maxStreak, setMaxStreak] = useState(0)
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null)
  const [showResult, setShowResult] = useState(false)
  const [isCorrect, setIsCorrect] = useState(false)
  const [showFact, setShowFact] = useState(false)
  const [gameOver, setGameOver] = useState(false)
  const [timeLeft, setTimeLeft] = useState(TIME_PER_QUESTION)
  const [isTimerRunning, setIsTimerRunning] = useState(false)
  const [soundEnabled, setSoundEnabled] = useState(true)
  const [correctAnswers, setCorrectAnswers] = useState(0)
  const [completedCards, setCompletedCards] = useState<Array<{ id: string; correct: boolean }>>([])
  const containerRef = useRef<HTMLDivElement>(null)

  const sound = (type: Parameters<typeof playSound>[0]) => {
    if (soundEnabled) playSound(type)
  }

  // Timer
  useEffect(() => {
    if (!isTimerRunning || showResult || gameOver) return

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          handleAnswer(null)
          return TIME_PER_QUESTION
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [isTimerRunning, showResult, gameOver])

  // Auto-start Open Water quiz when module param
  useEffect(() => {
    if (openWaterModuleId && !selectedTopic && !openWaterTopic) {
      const mod = getModuleById(openWaterModuleId)
      if (mod) {
        const cards = getRandomModuleCards(openWaterModuleId, CARDS_PER_ROUND)
        const topic: QuizTopic = {
          id: `openwater-${openWaterModuleId}`,
          name: `Open Water: ${mod.title}`,
          emoji: mod.emoji,
          color: 'from-cyan-500 to-blue-600',
          description: mod.description,
          cards,
        }
        setOpenWaterTopic(topic)
        setSelectedTopic(topic)
        const shuffled = [...cards].sort(() => Math.random() - 0.5).map(shuffleOptions)
        setCurrentCards(shuffled.slice(0, CARDS_PER_ROUND))
        setCurrentIndex(0)
        setScore(0)
        setScoreGained(null)
        setStreak(0)
        setMaxStreak(0)
        setCorrectAnswers(0)
        setCompletedCards([])
        setGameOver(false)
        setShowResult(false)
        setSelectedAnswer(null)
        setTimeLeft(TIME_PER_QUESTION)
        setIsTimerRunning(true)
      }
    }
  }, [openWaterModuleId])

  // Start game with selected topic
  const startGame = (topic: QuizTopic) => {
    setSelectedTopic(topic)
    setOpenWaterTopic(null)
    const shuffled = [...topic.cards].sort(() => Math.random() - 0.5).map(shuffleOptions)
    setCurrentCards(shuffled.slice(0, CARDS_PER_ROUND))
    setCurrentIndex(0)
    setScore(0)
    setScoreGained(null)
    setStreak(0)
    setMaxStreak(0)
    setCorrectAnswers(0)
    setCompletedCards([])
    setGameOver(false)
    setShowResult(false)
    setSelectedAnswer(null)
    setTimeLeft(TIME_PER_QUESTION)
    setIsTimerRunning(true)
    containerRef.current?.focus()
  }

  // Handle answer selection
  const handleAnswer = useCallback((answer: string | null) => {
    if (showResult) return

    setIsTimerRunning(false)
    setSelectedAnswer(answer)
    setShowResult(true)

    const currentCard = currentCards[currentIndex]
    const correct = answer === currentCard.answer

    setIsCorrect(correct)

    if (correct) {
      sound('match')
      vibrate(50)
      const timeBonus = Math.floor(timeLeft * 2)
      const streakBonus = streak * 10
      const points = 100 + timeBonus + streakBonus
      setScore(prev => prev + points)
      setScoreGained(points)
      setStreak(prev => prev + 1)
      setMaxStreak(prev => Math.max(prev, streak + 1))
      setCorrectAnswers(prev => prev + 1)
      setCompletedCards(prev => [...prev, { id: currentCard.id, correct: true }])
    } else {
      sound('wrong')
      vibrate(100)
      setStreak(0)
      setCompletedCards(prev => [...prev, { id: currentCard.id, correct: false }])
    }

    // Show fact immediately
    setShowFact(true)
  }, [showResult, currentCards, currentIndex, streak, timeLeft, soundEnabled])

  // Auto-avanzar cuando la respuesta es correcta
  useEffect(() => {
    if (!showResult || !isCorrect || gameOver) return
    const t = setTimeout(() => {
      setScoreGained(null)
      nextCard()
    }, AUTO_ADVANCE_DELAY_MS)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showResult, isCorrect, gameOver])

  const currentCard = currentCards[currentIndex]

  // =============================================
  // KEYBOARD INTERACTION
  // =============================================
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // During quiz - answer with 1/2/3/4 or A/B/C/D
      if (selectedTopic && !gameOver && currentCard && !showResult) {
        const keyMap: Record<string, number> = {
          '1': 0, '2': 1, '3': 2, '4': 3,
          'a': 0, 'b': 1, 'c': 2, 'd': 3,
        }
        const idx = keyMap[e.key.toLowerCase()]
        if (idx !== undefined && currentCard.options[idx]) {
          e.preventDefault()
          handleAnswer(currentCard.options[idx])
        }
      }

      // After wrong answer - Enter/Space to go next
      if (showResult && !isCorrect && !gameOver) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          nextCard()
        }
      }

      // Game over - Enter to replay, Escape to topics
      if (gameOver && selectedTopic) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          startGame(selectedTopic)
        }
        if (e.key === 'Escape') {
          e.preventDefault()
          backToTopics()
        }
      }

      // Escape during game = back to topics
      if (selectedTopic && !gameOver && e.key === 'Escape') {
        e.preventDefault()
        backToTopics()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedTopic, gameOver, currentCard, showResult, isCorrect, handleAnswer])

  // Next card
  const nextCard = () => {
    setShowFact(false)
    setShowResult(false)
    setSelectedAnswer(null)
    setScoreGained(null)

    if (currentIndex + 1 >= currentCards.length) {
      // Game over
      setGameOver(true)
      setIsTimerRunning(false)
      sound('win')
      vibrate([100, 50, 100])

      // Mojiland: intentar completar objetivo de Open Water
      if (openWaterModuleId && selectedTopic) {
        // correctAnswers se actualiza en handleAnswer; aquí ya incluye la última respuesta
        mojilandStore.tryCompleteFromQuiz(openWaterModuleId, correctAnswers, currentCards.length)
      }
      
      // Submit score
      if (score > 0 && selectedTopic) {
        const topicLevel = quizTopics.indexOf(selectedTopic)
        leaderboardStore.submitGameScore('quizmoji', score, topicLevel >= 0 ? topicLevel : 0)
        gamificationStore.recordGameResult('quizmoji', score, topicLevel >= 0 ? topicLevel : 0, true)
      }
    } else {
      setCurrentIndex(prev => prev + 1)
      setTimeLeft(TIME_PER_QUESTION)
      setIsTimerRunning(true)
    }
  }

  // Reset to topic selection (o volver a Mojiland si venimos de ahí)
  const backToTopics = () => {
    setSelectedTopic(null)
    setOpenWaterTopic(null)
    setGameOver(false)
    setCurrentCards([])
    setCompletedCards([])
  }

  const progress = ((currentIndex + 1) / currentCards.length) * 100
  const isOpenWater = !!openWaterModuleId
  /** Profundidad en metros para tema buceo: bajando con cada card (0→40m) */
  const depthMeters = Math.round(((currentIndex + 1) / currentCards.length) * 40)
  const isAutoAdvancing = showResult && isCorrect

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      className={`min-h-screen flex flex-col relative overflow-hidden outline-none ${selectedTopic ? `bg-gradient-to-br ${selectedTopic.color}` : 'bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500'}`}
    >
      {/* Burbujas decorativas (Open Water) */}
      {isOpenWater && selectedTopic && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
    {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-white/30 bubble-float"
              style={{
                width: `${8 + (i % 3) * 4}px`,
                height: `${8 + (i % 3) * 4}px`,
                left: `${5 + (i * 13) % 90}%`,
                top: `${15 + (i * 17) % 70}%`,
                animationDelay: `${i * 0.4}s`,
              }}
            />
          ))}
        </div>
      )}
      {/* Header - tema Open Water: más info de progreso */}
      <header className="sticky top-0 z-40 bg-white/10 backdrop-blur-md border-b border-white/20">
        <div className="max-w-md mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {selectedTopic ? (
                <button
                  onClick={backToTopics}
                  className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                >
                  <span className="text-white text-sm">←</span>
                </button>
              ) : (
                <Link
                  to="/"
                  className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                >
                  <span className="text-white text-sm">←</span>
                </Link>
              )}
              <span className="text-2xl">{selectedTopic?.emoji || '🧠'}</span>
              <span className="font-bold text-xl text-white">
                {selectedTopic?.name || 'QuizMoji'}
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              {selectedTopic && !gameOver && (
                <div className="flex items-center gap-2">
                  <div className="relative px-3 py-1 rounded-full bg-white/20 text-white text-sm font-bold">
                    ⭐ {score}
                    {scoreGained && (
                      <span className="absolute -top-1 -right-1 text-green-300 animate-bounce text-xs font-black">
                        +{scoreGained}
                      </span>
                    )}
                  </div>
                  {isOpenWater && (
                    <div className="px-2 py-1 rounded-full bg-cyan-500/40 text-white text-xs font-bold">
                      {depthMeters}m
                    </div>
                  )}
                </div>
              )}
              <Link to="/leaderboard" className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30">
                <span>🏆</span>
              </Link>
              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30"
              >
                <span>{soundEnabled ? '🔊' : '🔇'}</span>
              </button>
            </div>
          </div>
          {/* Progreso */}
          {selectedTopic && !gameOver && (
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 h-2 bg-white/20 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 rounded-full ${
                    isOpenWater ? 'bg-gradient-to-r from-cyan-300 to-blue-500' : 'bg-white'
                  }`}
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-white font-bold text-xs shrink-0">
                {currentIndex + 1}/{currentCards.length}
              </span>
            </div>
          )}
        </div>
      </header>

      <div className="flex-1 max-w-md mx-auto px-4 py-6 w-full">
        {!selectedTopic ? (
          // Topic Selection
          <div className="space-y-4">
            <div className="text-center mb-6">
              <h1 className="text-3xl font-bold text-white mb-2">🧠 QuizMoji</h1>
              <p className="text-white/70">Learn new things with flashcards!</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {quizTopics.map(topic => (
                <button
                  key={topic.id}
                  onClick={() => startGame(topic)}
                  className={`p-4 rounded-2xl bg-gradient-to-br ${topic.color} hover:scale-105 transition-all shadow-lg`}
                >
                  <span className="text-4xl block mb-2">{topic.emoji}</span>
                  <span className="text-white font-bold">{topic.name}</span>
                  <span className="text-white/70 text-xs block">{topic.cards.length} cards</span>
                </button>
              ))}
            </div>

            {/* Leaderboard */}
            <div className="mt-6">
              <GameLeaderboard
                game="quizmoji"
                gameName="QuizMoji"
                emoji="🧠"
                gradient="from-purple-500 to-pink-500"
                limit={5}
              />
            </div>
          </div>
        ) : gameOver ? (
          // Game Over Screen
          <div className="flex flex-col items-center justify-center h-full">
            <div className="bg-white rounded-3xl p-8 text-center shadow-2xl max-w-sm w-full">
              <span className="text-6xl block mb-4">
                {correctAnswers === CARDS_PER_ROUND ? '🏆' : correctAnswers >= CARDS_PER_ROUND / 2 ? '🎉' : '📚'}
              </span>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                {correctAnswers === CARDS_PER_ROUND ? 'Perfect!' : correctAnswers >= CARDS_PER_ROUND / 2 ? 'Great Job!' : 'Keep Learning!'}
              </h2>

              <div className="grid grid-cols-3 gap-2 my-4">
                <div className="bg-gray-100 rounded-xl p-3">
                  <p className="text-2xl font-bold text-purple-600">{score}</p>
                  <p className="text-xs text-gray-500">Score</p>
                </div>
                <div className="bg-gray-100 rounded-xl p-3">
                  <p className="text-2xl font-bold text-green-600">{correctAnswers}/{CARDS_PER_ROUND}</p>
                  <p className="text-xs text-gray-500">Correct</p>
                </div>
                <div className="bg-gray-100 rounded-xl p-3">
                  <p className="text-2xl font-bold text-orange-600">{maxStreak}</p>
                  <p className="text-xs text-gray-500">Best Streak</p>
                </div>
              </div>

              <div className="flex flex-col gap-2 mt-4">
                <div className="flex gap-2">
                  <button
                    onClick={() => startGame(selectedTopic)}
                    className={`flex-1 py-3 rounded-xl bg-gradient-to-r ${selectedTopic.color} text-white font-bold hover:opacity-90 transition-opacity flex items-center justify-center gap-2`}
                  >
                    🔄 Repetir
                    <kbd className="px-1.5 py-0.5 rounded bg-white/20 text-[10px] font-mono">Enter</kbd>
                  </button>
                  <button
                    onClick={backToTopics}
                    className="px-4 py-3 rounded-xl bg-gray-200 text-gray-700 font-bold hover:bg-gray-300 transition-colors flex items-center justify-center gap-1"
                  >
                    Temas
                    <kbd className="px-1.5 py-0.5 rounded bg-gray-300 text-[10px] font-mono">Esc</kbd>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : currentCard ? (
          // Quiz Card
          <div className="space-y-4">
            {/* Registro de cards completadas */}
            {currentCards.length > 0 && (
              <div className="flex justify-center gap-1.5 flex-wrap">
                {currentCards.map((_, idx) => {
                  const comp = completedCards[idx]
                  const isCurrent = idx === currentIndex
                  return (
                    <div
                      key={idx}
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                        comp
                          ? comp.correct
                            ? 'bg-green-500 text-white shadow-lg'
                            : 'bg-red-400 text-white'
                          : isCurrent
                            ? 'bg-white/90 text-cyan-600 ring-2 ring-white'
                            : 'bg-white/30 text-white/70'
                      }`}
                    >
                      {comp ? (comp.correct ? '✓' : '✗') : idx + 1}
                    </div>
                  )
                })}
              </div>
            )}

            {/* Timer o profundidad (Open Water) */}
            <div className="flex justify-center">
              {isOpenWater ? (
                <div className="flex flex-col items-center">
                  <div className="w-20 h-20 rounded-full flex flex-col items-center justify-center bg-gradient-to-b from-cyan-400/80 to-blue-600/80 text-white border-4 border-white/50 shadow-xl">
                    <span className="text-2xl font-black">{depthMeters}</span>
                    <span className="text-xs font-bold">m</span>
                  </div>
                  <p className="text-white/90 text-xs font-bold mt-1">Profundidad</p>
                </div>
              ) : (
                <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold transition-all ${
                  timeLeft <= 5 ? 'bg-red-500 animate-pulse text-white' : 'bg-white/20 text-white'
                }`}>
                  {timeLeft}
                </div>
              )}
            </div>

            {/* Streak indicator */}
            {streak > 0 && (
              <div className="flex justify-center">
                <div className="px-4 py-1.5 bg-amber-400 rounded-full text-white font-bold text-sm shadow-lg animate-pulse">
                  🔥 {streak} racha!
                </div>
              </div>
            )}

            {/* Auto-advance countdown cuando correcto */}
            {isAutoAdvancing && (
              <div className="flex justify-center">
                <div className="px-4 py-2 bg-green-500/90 rounded-xl text-white font-bold text-sm animate-pulse">
                  ✓ ¡Correcto! Siguiente en breve...
                </div>
              </div>
            )}

            {/* Question Card */}
            <div className="bg-white rounded-3xl p-6 shadow-xl">
              <div className="text-center mb-6">
                <span className="text-5xl block mb-3">{currentCard.emoji}</span>
                <h3 className="text-xl font-bold text-gray-800">{currentCard.question}</h3>
              </div>

              {/* Answer Options */}
              <div className="space-y-3">
                {currentCard.options.map((option, idx) => {
                  const isSelected = selectedAnswer === option
                  const isCorrectAnswer = option === currentCard.answer
                  const showCorrectness = showResult

                  let buttonClass = 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                  
                  if (showCorrectness) {
                    if (isCorrectAnswer) {
                      // La respuesta correcta siempre en verde y destacada
                      buttonClass = 'bg-gradient-to-r from-green-500 to-emerald-500 text-white ring-4 ring-green-300 shadow-lg'
                    } else if (isSelected) {
                      // La seleccionada incorrecta en rojo
                      buttonClass = 'bg-red-500 text-white opacity-80'
                    } else {
                      // Las demás atenuadas
                      buttonClass = 'bg-gray-100 text-gray-400 opacity-60'
                    }
                  }

                  return (
                    <button
                      key={idx}
                      onClick={() => !showResult && handleAnswer(option)}
                      disabled={showResult}
                      className={`w-full p-4 rounded-xl text-left font-medium transition-all duration-200 ${buttonClass}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <kbd className={`inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold shrink-0 ${
                            showCorrectness
                              ? isCorrectAnswer ? 'bg-white/30 text-white' : 'bg-black/10 text-current opacity-50'
                              : 'bg-gray-200 text-gray-600 border border-gray-300'
                          }`}>{idx + 1}</kbd>
                          <span>{option}</span>
                        </span>
                        {showCorrectness && isCorrectAnswer && (
                          <span className="text-xl">✓</span>
                        )}
                        {showCorrectness && isSelected && !isCorrectAnswer && (
                          <span className="text-xl">✗</span>
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>

              {/* Fact reveal */}
              {showFact && (
                <div className={`mt-4 p-4 rounded-xl transition-all animate-correct-pop ${isCorrect ? 'bg-green-50 border-2 border-green-300 shadow-lg' : 'bg-amber-50 border-2 border-amber-200'}`}>
                  <p className="text-sm">
                    <span className={`font-bold ${isCorrect ? 'text-green-700' : 'text-amber-800'}`}>
                      {isCorrect ? '✓ ¡Correcto!' : '✗ Incorrecto'}
                    </span>
                    <span className="text-gray-600"> · 💡 {currentCard.fact}</span>
                  </p>
                </div>
              )}

              {/* Next button - solo si es incorrecto (correcto = auto-avanza) */}
              {showResult && !isCorrect && (
                <button
                  onClick={nextCard}
                  className="w-full mt-4 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                >
                  {currentIndex + 1 >= currentCards.length ? 'Ver resultados' : 'Siguiente pregunta'} →
                  <kbd className="ml-2 px-2 py-0.5 rounded bg-white/20 text-xs font-mono">Enter</kbd>
                </button>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
})

export default QuizMoji
